import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Star, Loader2 } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { businessApi } from '@/lib/api';
import { toast } from 'sonner';

export function ClientReviewSettings() {
  const { nav } = useBusinessNav();
  const { currentBusiness, refreshUser } = useApp();
  const [saving, setSaving] = useState(false);

  const handleToggle = async (checked: boolean) => {
    if (!currentBusiness) return;

    setSaving(true);
    const response = await businessApi.update(currentBusiness.id, {
      client_reviews_enabled: checked,
    } as Record<string, unknown>);

    if (response.data) {
      await refreshUser();
      toast.success(checked ? 'Client reviews enabled' : 'Client reviews disabled');
    } else {
      toast.error('Failed to update settings');
    }
    setSaving(false);
  };

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Client Reviews</h1>
        <p className="text-sm text-muted-foreground">
          Allow your clients to leave feedback after paying invoices.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Star className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Enable Client Reviews</CardTitle>
              <CardDescription>
                Turn on client review functionality, allowing clients to leave reviews on paid invoices
              </CardDescription>
            </div>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={currentBusiness?.client_reviews_enabled ?? false}
                onCheckedChange={handleToggle}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            When enabled, clients will see a review form after successfully paying an invoice.
            Reviews can help build trust with potential customers and provide valuable feedback.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
